/**
 * @fileoverview Present registry folder organization and delegate explicit entry actions to the workspace. Shared registry metadata is distinct from canonical document storage and version history. The parent owns mutation execution and accepted state; this component coordinates organization controls and current selection.
 */

import React, { useEffect, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import './DocumentRegistryOrganization.css';

export { documentTypeIcon } from './DocumentFileTypeIcon';

export const EMPTY_ORGANIZATION_FILTERS = {
  fileType: '',
  owner: '',
  sizeMin: '',
  sizeMax: '',
  tags: '',
  sharing: '',
  source: '',
  createdFrom: '',
  createdTo: '',
  modifiedFrom: '',
  modifiedTo: '',
  trashStatus: 'active',
  favorite: false,
  pinned: false,
};

export const REGISTRY_COLUMN_OPTIONS = [
  ['name', 'Name'],
  ['location', 'Location'],
  ['created', 'Created'],
  ['modified', 'Modified'],
  ['size', 'Size'],
  ['type', 'Type'],
  ['owner', 'Owner'],
  ['versions', 'Versions'],
  ['tags', 'Tags'],
  ['source', 'Source'],
];

const SORT_OPTIONS = [
  ['modified_desc', 'Newest first'],
  ['modified_asc', 'Oldest first'],
  ['name_asc', 'Name A–Z'],
  ['name_desc', 'Name Z–A'],
];



export function RegistryOrganizationControls({
  sort,
  viewMode,
  onSortChange,
  onViewModeChange,
}) {
  const supportedSort = SORT_OPTIONS.some(([value]) => value === sort) ? sort : 'modified_desc';
  const supportedView = viewMode === 'grid' ? 'grid' : 'list';

  return (
    <section className="documents-organization-controls" aria-label="Organize document results">
      <div className="documents-organization-primary">
        <label className="documents-organization-sort">
          <span className="documents-visually-hidden">Sort documents</span>
          <select aria-label="Sort documents" value={supportedSort} onChange={(event) => onSortChange(event.target.value)}>
            {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {onViewModeChange && <div className="documents-organization-view" role="group" aria-label="Document view">
          {[
            ['list', 'pi pi-list', 'List'],
            ['grid', 'pi pi-th-large', 'Grid'],
          ].map(([value, icon, label]) => (
            <button key={value} type="button" className={supportedView === value ? 'is-active' : ''} aria-pressed={supportedView === value} onClick={() => onViewModeChange(value)} title={`${label} view`}>
              <AppIcon icon={icon} aria-hidden="true" /><span>{label}</span>
            </button>
          ))}
        </div>}
      </div>
    </section>
  );
}



export function DocumentOrganizationActions({ document, busy, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState((document.tags || []).join(', '));
  const [description, setDescription] = useState(document.description || '');
  const [customMetadata, setCustomMetadata] = useState(JSON.stringify(document.custom_metadata || {}, null, 2));
  const [error, setError] = useState('');
  useEffect(() => {
    setTags((document.tags || []).join(', '));
    setDescription(document.description || '');
    setCustomMetadata(JSON.stringify(document.custom_metadata || {}, null, 2));
  }, [document]);
  const save =

  () => {
    let metadata;
    try {
      metadata = JSON.parse(customMetadata || '{}');
    } catch (_error) {
      setError('Custom metadata must be valid JSON.');
      return;
    }
    setError('');
    onUpdate(document, {
      tags: tags.split(',').map((value) => value.trim()).filter(Boolean),
      description,
      custom_metadata: metadata,
    }).then(() => setEditing(false)).catch((updateError) => setError(updateError?.response?.data?.error || updateError.message || 'Could not save metadata.'));
  };
  return (
    <div className="documents-organization-item-actions" onClick={(event) => event.stopPropagation()}>
      <button type="button" disabled={busy} className={document.favorite ? 'is-active' : ''} aria-label={`${document.favorite ? 'Remove' : 'Add'} ${document.filename} ${document.favorite ? 'from' : 'to'} favorites`} aria-pressed={Boolean(document.favorite)} onClick={() => onUpdate(document, { favorite: !document.favorite })}><AppIcon icon={document.favorite ? 'pi pi-star-fill' : 'pi pi-star'} /></button>
      <button type="button" disabled={busy} className={document.pinned ? 'is-active' : ''} aria-label={`${document.pinned ? 'Unpin' : 'Pin'} ${document.filename}`} aria-pressed={Boolean(document.pinned)} onClick={() => onUpdate(document, { pinned: !document.pinned })}><AppIcon icon="pi pi-thumbtack" /></button>
      <button type="button" disabled={busy} aria-label={`Edit metadata for ${document.filename}`} onClick={() => setEditing((value) => !value)}><AppIcon icon="pi pi-tag" /></button>
      {editing && <div className="documents-organization-editor" role="dialog" aria-label={`Metadata for ${document.filename}`}>
        <header><strong>Document details</strong><button type="button" aria-label="Close metadata editor" onClick={() => setEditing(false)}>×</button></header>
        <label><span>Tags</span><input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="Comma separated" /></label>
        <label><span>Description</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} rows="3" /></label>
        <label><span>Custom metadata (JSON)</span><textarea value={customMetadata} onChange={(event) => setCustomMetadata(event.target.value)} rows="5" spellCheck="false" /></label>
        {error && <span role="alert">{error}</span>}
        <footer><button type="button" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save details'}</button></footer>
      </div>}
    </div>
  );
}



export function formatFileSize(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}
